<template>
  <InputGroup
    :bs="bs"
    :readonly="readonly"
    v-bind="$attrs"
  >
    <template
      v-if="$slots.left"
      #left
    >
      <slot name="left" />
    </template>
    <template #customWrapper="{ leftWidth, rightWidth }">
      <NewBsBox
        :disabled="disabled || null"
        :data-error="$attrs['data-error'] || null"
        :data-active="focused || null"
        :context="{ bordered }"
        :bs="{
          'cmp.inputGroup': 'inputWrapper',
          'cmp.inputGroup.rangeInput': true,
          display: 'flex', alignItems: 'center',
          border: readonly ? 'none' : null,
        }"
      >
        <NewBsBox :bs="{ width: leftWidth, height: 30 }" />
        <NewBsBox
          ref="leftInput"
          :bs="{ 'cmp.inputGroup': 'rangeInputItem' }"
          :disabled="disabled || null"
          :as="readonly ? 'div' : 'input'"
          v-bind="leftInputAttrs"
          @focus="() => focus.left = true"
          @blur="() => focus.left = false"
        >
          {{ left }}
        </NewBsBox>
        <NewBsBox :bs="{ color: 'mute', fontSize: 14 }">
          至
        </NewBsBox>
        <NewBsBox
          ref="rightInput"
          :bs="{ 'cmp.inputGroup': 'rangeInputItem' }"
          :disabled="disabled || null"
          :as="readonly ? 'div' : 'input'"
          v-bind="rightInputAttrs"
          @focus="() => focus.right = true"
          @blur="() => focus.right = false"
        >
          {{ right }}
        </NewBsBox>
        <NewBsBox :bs="{ width: rightWidth, height: 30 }" />
      </NewBsBox>
      <slot />
    </template>
    <template
      #right="{ width }"
    >
      <BeerIcon
        v-if="clearAble"
        :size="14"
        :bs="{
          mx: 12, transform: 'rotate(45deg)',
          fill: 'ash',
          '&:hover': { fill: 'mute' }
        }"
        icon="add-f"
        @click="handleClear"
      />
      <UnitCell
        v-if="unit"
        :width="width"
        :unit-enums="unitEnums"
        :current-uit="currentUit"
        :on-select-unit="selectUnit"
      />
    </template>
  </InputGroup>
</template>

<script lang="ts">
  import {
    defineComponent, PropType,
    reactive, computed,
  } from 'vue'
  import { isNil } from 'lodash'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
  import InputGroup from '../InputGroup/InputGroup.vue'
  import UnitCell from './UnitCell.vue'
  import { BeerStyleObj } from '../../../core'

  /**
   * 区间输入框
   * @see 继承InputGroup #InputGroup
   */
  export default defineComponent({
    name: 'RangeInput',
    components: {
      BeerIcon,
      NewBsBox,
      InputGroup,
      UnitCell,
    },
    inheritAttrs: false,
    props: {
      /**
       * @ignore
       *  容器 Bs，传递给 InputGroup
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * @ignore
       * 是否只读显示
       */
      readonly: {
        type: Boolean,
        default: false,
      },
      /**
       * @ignore
       * 是否带边框
       */
      bordered: {
        type: Boolean,
        default: true,
      },
      /**
       * 左边值
       */
      left: [String, Number],
      /**
       * 右边值
       */
      right: [String, Number],
      /**
       * 单位值，可以不传，就不显示
       */
      unit: [String, Number],
      /**
       * 是否禁止
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * 单位选择列表
       */
      unitEnums: {
        type: Array as PropType<string[]>,
      },
      /**
       * 自定义左右 input 的事件
       */
      getInputProps: {
        type: Function,
      },
      /**
       * @ignore
       * placeholder 的快捷 props，作为 inputProps 的默认值
       */
      placeholder: {
        type: String,
        default: '不限',
      },
    },
    emits: {
      /**
       * 左边值更新, v-model:left
       */
      'update:left': null,
      /**
       * 右边值更新, v-model:right
       */
      'update:right': null,
      /**
       * 单位更新 v-model:unit
       */
      'update:unit': null,
      /**
       * 清除值的操作
       */
      'click:clear': null,
    },
    setup(props, { emit }) {
      const focus = reactive({
        left: false,
        right: false,
      })
      const focused = computed(() => focus.left || focus.right)
      const currentUit = computed(() => {
        if (props.unit) {
          return props.unit
        }
        if (props.unitEnums && props.unitEnums.length > 0) {
          return props.unitEnums[0]
        }
        return '-'
      })
      const clearAble = computed(() => {
        if (props.readonly) {
          return false
        }
        return props.left || props.right
      })
      const leftInputAttrs = computed(() => {
        const attr = {
          onInput: (e: KeyboardEvent) => {
            emit('update:left', (e.target as HTMLInputElement).value)
          },
          placeholder: props.placeholder,
          ...props.getInputProps?.('left'),
        }
        if (props.readonly) {
          attr.placeholder = null
        }
        if (!isNil(props.left)) {
          attr.value = props.left
        }
        return attr
      })
      const rightInputAttrs = computed(() => {
        const attr = {
          onInput: (e: KeyboardEvent) => {
            emit('update:right', (e.target as HTMLInputElement).value)
          },
          placeholder: props.placeholder,
          ...props.getInputProps?.('right'),
        }
        if (props.readonly) {
          attr.placeholder = null
        }
        if (!isNil(props.right)) {
          attr.value = props.right
        }
        return attr
      })

      return {
        focus,
        focused,
        currentUit,
        leftInputAttrs,
        clearAble,
        rightInputAttrs,
        handleClear: () => {
          emit('click:clear')
          emit('update:left', undefined)
          emit('update:right', undefined)
        },
        selectUnit: (unit: string) => {
          emit('update:unit', unit)
        },
      }
    },
  })
</script>
